import type { BeneficiaryAgencyScope } from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryAgencyScope';
import AbstractFilterView, { type AbstractFilterViewArgs } from '@UI/Helpers/Filters/AbstractFilterView';

export type BeneficiaryFilterViewArgs = AbstractFilterViewArgs & {
	readonly search?: string;
	readonly assignedUserId?: number | null;
	readonly includeArchived?: boolean;
	readonly agencyScope?: BeneficiaryAgencyScope;
	readonly facilityIds?: readonly number[];
};

/**
 * `assignedUserId` is the single server parameter behind the "professional affected" filter: `null`
 * reads every dossier of the scope, the current user's id reads "my dossiers", any other id reads
 * one colleague's dossiers. `agencyScope` is what the "Agence" dropdown shows and `facilityIds` what is
 * sent to the server (empty means every assigned facility): the two are always written together.
 */
class BeneficiaryFilterView extends AbstractFilterView<BeneficiaryFilterViewArgs> {
	readonly search: string;
	readonly assignedUserId: number | null;
	readonly includeArchived: boolean;
	readonly agencyScope: BeneficiaryAgencyScope;
	readonly facilityIds: readonly number[];

	constructor(args: BeneficiaryFilterViewArgs) {
		super(args);

		this.search = args.search ?? '';
		this.assignedUserId = args.assignedUserId ?? null;
		this.includeArchived = args.includeArchived ?? false;
		this.agencyScope = args.agencyScope ?? { type: 'all' };
		this.facilityIds = args.facilityIds ?? [];

		Object.freeze(this);
	}

	static createDefault(): BeneficiaryFilterView {
		return new BeneficiaryFilterView({});
	}

	override get queryParams(): URLSearchParams {
		const params = super.queryParams;

		if (this.search.length > 0) {
			params.set('search', this.search);
		}

		if (this.assignedUserId !== null) {
			params.set('assignedUserId', String(this.assignedUserId));
		}

		// The server never excludes archived beneficiaries by default: the client sends `status=active`
		// itself, so "include archived" stays expressible as the absence of this parameter.
		if (!this.includeArchived) {
			params.set('status', 'active');
		}

		for (const facilityId of this.facilityIds) {
			params.append('facilityIds', String(facilityId));
		}

		return params;
	}

	/** Number of criteria that narrow the results; sorting never counts. */
	get activeFilterCount(): number {
		return [
			this.search.length > 0,
			this.assignedUserId !== null,
			this.includeArchived,
			this.agencyScope.type !== 'all',
		].filter(Boolean).length;
	}

	setSearch(search: string): this {
		return this.cloneWith({ search, page: 0 });
	}

	setAssignedUserId(assignedUserId: number | null): this {
		return this.cloneWith({ assignedUserId, page: 0 });
	}

	setIncludeArchived(includeArchived: boolean): this {
		return this.cloneWith({ includeArchived, page: 0 });
	}

	setAgencyScope(agencyScope: BeneficiaryAgencyScope, facilityIds: readonly number[]): this {
		return this.cloneWith({ agencyScope, facilityIds, page: 0 });
	}

	hasActiveFilters(): boolean {
		return this.activeFilterCount > 0;
	}
}

export default BeneficiaryFilterView;
